import React, { useState } from 'react';
import {
  Container, Grid, Card, CardContent, Typography, Button,
  Box, List, ListItem, ListItemText, Paper, Chip, CircularProgress, Alert
} from '@mui/material';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import DownloadIcon from '@mui/icons-material/Download';
import StarIcon from '@mui/icons-material/Star';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { createSubscriptionLink, SUBSCRIPTIONS } from '../services/paymentService';

const MembersDashboard = () => {
  const purchasedSigns = ['Aries', 'Taurus']; // Example data
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState('');

  // User info - in production, get from auth context
  const userEmail = 'user@example.com'; // TODO: Get from auth
  const userId = 'user123'; // TODO: Get from auth

  const handleSubscribe = async (planType) => {
    setSubscriptionLoading(true);
    setSubscriptionError('');

    try {
      const plan = planType === 'monthly'
        ? SUBSCRIPTIONS.MONTHLY_MEMBERSHIP
        : SUBSCRIPTIONS.YEARLY_MEMBERSHIP;

      const response = await createSubscriptionLink({
        email: userEmail,
        user_id: userId,
        plan_name: plan.name,
        amount_cents: plan.amount_cents,
        currency: plan.currency,
        interval: plan.interval
      });

      if (response.success && response.url) {
        window.location.href = response.url;
      } else {
        setSubscriptionError(response.error || 'Failed to create subscription');
        setSubscriptionLoading(false);
      }
    } catch (err) {
      setSubscriptionError(err.message || 'Something went wrong. Please try again.');
      console.error('Subscription error:', err);
      setSubscriptionLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" gutterBottom>
        Your Dashboard
      </Typography>

      <Grid container spacing={3}>
        {/* Welcome Card */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, background: 'linear-gradient(135deg, #667eea22 0%, #764ba244 100%)' }}>
            <Typography variant="h5" gutterBottom>
              Welcome back!
            </Typography>
            <Typography color="text.secondary">
              Your 2026 forecasts are ready to explore. May the stars guide your journey.
            </Typography>
          </Paper>
        </Grid>

        {/* Purchased Forecasts */}
        <Grid item xs={12} md={8}>
          <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
            Your Forecasts
          </Typography>

          <Grid container spacing={2}>
            {purchasedSigns.map((sign) => (
              <Grid item xs={12} key={sign}>
                <Card>
                  <CardContent>
                    <Grid container alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <Typography variant="h6">
                          {sign} Rising - Extended Forecast
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          60+ minute deep dive • PDF report • Birth chart
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ textAlign: 'right' }}>
                        <Button
                          startIcon={<PlayCircleIcon />}
                          variant="contained"
                          sx={{ mr: 1, mb: 1 }}
                        >
                          Watch Video
                        </Button>
                        <Button
                          startIcon={<DownloadIcon />}
                          variant="outlined"
                          sx={{ mb: 1 }}
                        >
                          Download PDF
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Add More Signs */}
          <Card sx={{ mt: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Want forecasts for other Rising Signs?
              </Typography>
              <Typography color="text.secondary" paragraph>
                Get the All Access Pass and unlock all 12 Rising Sign forecasts
              </Typography>
              <Button variant="contained" color="primary">
                Upgrade to All Access
              </Button>
            </CardContent>
          </Card>

          {/* Membership Upgrade */}
          <Card sx={{ mt: 3, background: 'linear-gradient(135deg, #667eea11 0%, #764ba222 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AutorenewIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  Join Ivy's Inner Circle
                </Typography>
              </Box>

              {subscriptionError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {subscriptionError}
                </Alert>
              )}

              <Typography color="text.secondary" paragraph>
                Get monthly updates, exclusive content, and personalized astrology guidance
              </Typography>

              <Grid container spacing={2}>
                {/* Monthly Plan */}
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Monthly
                      </Typography>
                      <Typography variant="h4" gutterBottom>
                        $9.99
                        <Typography variant="caption" color="text.secondary">
                          /month
                        </Typography>
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText
                            primary="Monthly forecast updates"
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Exclusive member content"
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Early access to new forecasts"
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      </List>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => handleSubscribe('monthly')}
                        disabled={subscriptionLoading}
                      >
                        {subscriptionLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          'Subscribe Monthly'
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Yearly Plan */}
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" sx={{ border: '2px solid', borderColor: 'primary.main' }}>
                    <CardContent>
                      <Chip
                        label="Save 17%"
                        color="primary"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="h6" gutterBottom>
                        Yearly
                      </Typography>
                      <Typography variant="h4" gutterBottom>
                        $99.99
                        <Typography variant="caption" color="text.secondary">
                          /year
                        </Typography>
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText
                            primary="All monthly benefits"
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Save $20/year"
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Priority support"
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      </List>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => handleSubscribe('yearly')}
                        disabled={subscriptionLoading}
                      >
                        {subscriptionLoading ? (
                          <CircularProgress size={20} sx={{ color: 'white' }} />
                        ) : (
                          'Subscribe Yearly'
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Quick Links */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Links
              </Typography>
              <List dense>
                <ListItem button>
                  <ListItemText primary="Find Your Rising Sign" />
                </ListItem>
                <ListItem button>
                  <ListItemText primary="2026 Major Dates" />
                </ListItem>
                <ListItem button>
                  <ListItemText primary="Mercury Retrograde Calendar" />
                </ListItem>
                <ListItem button>
                  <ListItemText primary="Join Facebook Group" />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Key Dates This Month
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Chip label="Jan 26" size="small" sx={{ mr: 1 }} />
                <Typography variant="body2" display="inline">
                  Neptune enters Aries
                </Typography>
              </Box>
              <Box sx={{ mt: 1 }}>
                <Chip label="Feb 13" size="small" sx={{ mr: 1 }} />
                <Typography variant="body2" display="inline">
                  Saturn enters Aries
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default MembersDashboard;